import { Box, Portal } from '@mui/material';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { useFullscreenContext } from '@/components/Fullscreen/fullscreen.context';
import { zIndexes } from '@/styles/zIndexes';

const OverlayContainerContext = createContext<
  (() => HTMLElement | null) | undefined
>(undefined);

export const ModalOverlayContainerContext = createContext<
  (() => HTMLElement | null) | undefined
>(undefined);

export function useOverlayContainer() {
  const layer = useContext(OverlayContainerContext);
  const modal = useContext(ModalOverlayContainerContext);
  // Modal hosts only change portal behavior within a diagram layer.
  return layer ? (modal ?? layer) : undefined;
}

/** Keep diagram overlays below dialogs without changing their open state. */
export function OverlayLayer({ children }: PropsWithChildren) {
  const parent = useOverlayContainer();
  const modal = useContext(ModalOverlayContainerContext);
  const inheritedContainer = modal ?? parent;
  const { isFullscreen } = useFullscreenContext();
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const container = useCallback(() => element, [element]);

  return (
    <OverlayContainerContext.Provider value={inheritedContainer ?? container}>
      {!inheritedContainer && (
        <Portal
          container={
            isFullscreen ? () => document.fullscreenElement : undefined
          }
        >
          <Box
            ref={setElement}
            data-diagram-overlay-layer
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: zIndexes.MODAL - 1,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          />
        </Portal>
      )}
      {children}
    </OverlayContainerContext.Provider>
  );
}
